import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { AspectRatio, Box, Flex, Icon, Image } from "@chakra-ui/react";
import * as PostAdditionalTypes from "../../../_constants/PostAdditionalTypes";
import * as SocialProviders from "../../../_constants/SocialProvider";
import SocialAccountPropType from "../../../_types//SocialAccount";
import PostPropType from "../../../_types/Post";
import SocialAccountAvatar from "../SocialAccountAvatar/SocialAccountAvatar";
import { getFixedPhotosVideosOrder } from "../PostEditor/helpers";

const SocialPostCardYouTubeShort = ({ account, post, time, ...rest }) => {
  const [hover, setHover] = useState(false);
  const type = account?.social_type;

  const additionalFields = useMemo(() => {
    const additional = post?.additional || [];

    return additional.find(
      (item) =>
        item.source === SocialProviders.YOUTUBE &&
        item.type === PostAdditionalTypes.POST
    );
  }, [post]);

  const mediaItems = useMemo(() => getFixedPhotosVideosOrder(post?.photos, post?.videos).items, [post?.photos, post?.videos]);

  const handleMouseEnter = useCallback(() => setHover(true), [setHover]);

  const handleMouseLeave = useCallback(() => setHover(false), [setHover]);

  if (!account || !post || !type) {
    return null;
  };

  const renderAvatar = (square) => (
    <Box
      sx={{
        position: "relative",
        width: square ? "32px" : "28px",
        height: square ? "32px" : "28px",
        lineHeight: square ? "32px" : "28px",
      }}
    >
      <SocialAccountAvatar
        sx={{
          width: "full",
          height: "full",
          fontSize: "16px",
          borderRadius: square ? "4px" : "full",
          border: square ? "1px solid #fff" : null,
        }}
        name={account.name}
        src={account.image}
        socialType={account.social_type}
      />
    </Box>
  );

  const renderTitle = () => (
    <Box
      sx={{
        fontSize: "13px",
        marginBottom: "10px",
      }}
    >
      {additionalFields?.title || ""}
    </Box>
  );

  const renderFooter = () => (
    <Flex
      sx={{
        position: "absolute",
        bottom: "0",
        width: "100%",
        alignItems: "flex-end",
        padding: "0 10px 10px",
        background: "linear-gradient(180deg,rgba(38,38,38,0) 0%,rgba(38,38,38,.6) 100%)",
        pointerEvents: "none",
        transition: "opacity 150ms ease-in-out",
        opacity: hover ? "0" : "1",
      }}
    >
      <Flex
        sx={{
          flex: "1",
          flexDirection: "column",
        }}
      >
        {renderTitle()}
        <Flex sx={{ alignItems: "center" }}>
          {renderAvatar()}
          <Box sx={{ marginLeft: "8px" }}>
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              {account.name}
            </Box>
            <Box sx={{ fontSize: "10px" }}>
              {time}
            </Box>
          </Box>
        </Flex>
      </Flex>
      <Flex
        sx={{
          flexDirection: "column",
          alignItems: "center",
          width: "32px",
        }}>
        <Box sx={{ fontSize: "18px", padding: "5px", color: "#fff" }}>
          <Icon viewBox="0 0 32 32" fill="currentcolor">
            <path fillRule="evenodd" clipRule="evenodd" d="M25.8411 11.9977C26.6778 11.9977 27.4597 12.414 27.9268 13.1082C28.6951 14.2498 28.6876 15.745 27.908 16.8789L27.1796 17.9383L27.8546 19.5037C28.257 20.4368 28.1597 21.5102 27.596 22.3558L26.4999 24V25.9977C26.4999 27.1023 25.6044 27.9977 24.4999 27.9977L11.9999 27.9976C10.8953 27.9976 9.99987 27.1022 9.99987 25.9976V12.8264C9.99987 11.9927 10.2604 11.1799 10.7449 10.5015L15.8632 3.33586C16.0776 3.03569 16.4721 2.92531 16.8112 3.07062C18.5762 3.82706 19.5347 5.74955 19.0766 7.6144L17.9999 11.9976L25.8411 11.9977ZM5 13.4999C3.89543 13.4999 3 14.3954 3 15.4999V25.9999C3 27.1045 3.89543 27.9999 5 27.9999H8V13.4999H5Z"></path>
          </Icon>
        </Box>
        <Box sx={{ fontSize: "20px", padding: "5px", color: "#fff" }}>
          <Icon viewBox="0 0 32 32" fill="currentcolor">
            <path fillRule="evenodd" clipRule="evenodd" d="M6.15895 20.0023C5.32221 20.0023 4.54031 19.586 4.07317 18.8918C3.30492 17.7502 3.31241 16.255 4.09205 15.1211L4.82045 14.0617L4.14538 12.4963C3.74297 11.5632 3.84031 10.4898 4.40399 9.64424L5.50013 8.00004L5.50013 6.00231C5.50013 4.89774 6.39557 4.00231 7.50014 4.00232L20.0001 4.00239C21.1047 4.0024 22.0001 4.89783 22.0001 6.0024L22.0001 19.1736C22.0001 20.0073 21.7396 20.8201 21.2551 21.4985L16.1368 28.6641C15.9224 28.9643 15.5279 29.0747 15.1888 28.9294C13.4238 28.1729 12.4653 26.2504 12.9234 24.3856L14.0001 20.0024L6.15895 20.0023ZM27 18.5001C28.1046 18.5001 29 17.6046 29 16.5001L29 6.00006C29 4.89549 28.1046 4.00006 27 4.00006L24 4.00006L24 18.5001L27 18.5001Z"></path>
          </Icon>
        </Box>
        <Box sx={{ fontSize: "20px", padding: "5px", color: "#fff" }}>
          <Icon viewBox="0 0 32 32" fill="currentcolor">
            <path fillRule="evenodd" clipRule="evenodd" d="M5.5 3C4.11929 3 3 4.11929 3 5.5V21.5C3 22.8807 4.11929 24 5.5 24H22.5L26.7474 28.5741C27.5513 29.4399 29 28.871 29 27.6895V24V5.5C29 4.11929 27.8807 3 26.5 3H5.5ZM8 10.5C8 9.67157 8.67157 9 9.5 9H22.5C23.3284 9 24 9.67157 24 10.5C24 11.3284 23.3284 12 22.5 12H9.5C8.67157 12 8 11.3284 8 10.5ZM8 16.5C8 15.6716 8.67157 15 9.5 15H18.5C19.3284 15 20 15.6716 20 16.5C20 17.3284 19.3284 18 18.5 18H9.5C8.67157 18 8 17.3284 8 16.5Z"></path>
          </Icon>
        </Box>
        <Box sx={{ fontSize: "20px", padding: "5px", color: "#fff", marginBottom: "10px" }}>
          <Icon viewBox="0 0 32 32" fill="currentcolor">
            <path fillRule="evenodd" clipRule="evenodd" d="M17.7375 5.26556L28.6745 15.2624C29.1083 15.6589 29.1083 16.3422 28.6745 16.7387L17.7375 26.7356C17.0958 27.3222 16.0628 26.8669 16.0628 25.9975V21.6217C16.0628 21.6217 16.0627 21.6217 16.0626 21.6217C9.92564 21.6217 6.69114 23.9378 5.1615 25.5968C4.80726 25.981 3.97329 25.7343 4.00015 25.2125C4.22558 20.8321 5.86088 10.8892 16.0626 10.8892C16.0627 10.8892 16.0628 10.8892 16.0628 10.8892V6.00368C16.0628 5.13426 17.0958 4.67898 17.7375 5.26556Z"></path>
          </Icon>
        </Box>
        {/* <Box sx={{ fontSize: "15px", padding: "5px", color: "#fff", marginBottom: "10px" }}>
          <FaEllipsisH />
        </Box> */}
        {renderAvatar(true)}
      </Flex>
    </Flex>
  );

  const renderMedia = () => {
    const maxItems = 1;
    const mediaItemsSliced = mediaItems.slice(0, maxItems);

    return (
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseOver={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {mediaItemsSliced.map((item) => (
          <Box key={item.data.url} sx={{ width: "100%", height: "100%" }}>
            {item.field === "videos" ? (
              <Box
                as="video"
                width="100%"
                height="100%"
                src={item.data.url}
                controls={hover}
                preload="metadata"
                poster={item.data.thumbUrl}
              />
            ) : (
              <Image
                width="100%"
                height="100%"
                objectFit="contain"
                src={item.data.url}
              />
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "300px",
        fontFamily: "Roboto, Arial, sans-serif",
      }}
      {...rest}
    >
      <AspectRatio
        ratio={9 / 16}
        sx={{
          overflow: "hidden",
          background: "#000",
          borderRadius: "8px",
        }}
      >
        <Box>
          {renderMedia()}
          {renderFooter()}
        </Box>
      </AspectRatio>
  </Box>
  );
};

SocialPostCardYouTubeShort.propTypes = {
  account: SocialAccountPropType.isRequired,
  post: PostPropType.isRequired,
  time: PropTypes.string,
};

export default SocialPostCardYouTubeShort;
